import React, {
  memo, useState, useCallback, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { POPUP } from 'utils/constants/popup';
import app from 'new-ui/app';
import { useObjectRef } from 'utils/hooks/useObjectRef';
import Questionnaire from 'components/Questionnaire';
import { Skeleton } from 'components/Skeleton';
import styles from './questionnaire.module.css';

app.editQuestion = (questions, onSave, onPopupClose) => {
  if (!app.openPopup) throw new Error('openPopup not ready!');

  const [question] = questions;

  if (question === 'treatments') {
    app.openPopup(POPUP.EDIT_QUESTION, { question, onClose: onPopupClose });
    return;
  }

  app.openPopup(POPUP.QUESTIONNAIRE, { questions, onSave, onClose: onPopupClose });
};

const QuestionnairePopup = memo(({
  questions,
  onSave,
  onClose,
}) => {
  const [questionProp] = questions;
  const [question, setQuestion] = useState({ id: questionProp });

  const onSaveRef = useObjectRef(onSave);
  const onCloseRef = useObjectRef(onClose);
  const questionsRef = useObjectRef(questions);

  const beforeNextStepAction = useCallback(({ profile, currentQuestionId }) => () => {
    onSaveRef.current({ profile, questionId: currentQuestionId });

    setQuestion((stateQuestion) => {
      // stateQuestion is not an object should be invariant
      // but in some cases it may still be possible the form submitted more than once
      const { id: stateQuestionId } = stateQuestion ?? {};
      if (!currentQuestionId) {
        return null;
      }
      const currentQuestionIndex = (questionsRef.current ?? []).findIndex(
        (questionId) => questionId === stateQuestionId,
      );
      if (currentQuestionIndex === -1 || !questionsRef.current[currentQuestionIndex + 1]) {
        return null;
      }
      return { id: questionsRef.current[currentQuestionIndex + 1] };
    });

    return false;
  }, [onSaveRef, questionsRef]);

  const targetQuestionNotFoundAction = useCallback((currentQuestionId) => () => {
    onSaveRef.current({ questionId: currentQuestionId, notFound: true });
  }, [onSaveRef]);

  useEffect(() => () => {
    if (onCloseRef.current) {
      onCloseRef.current();
    }
  }, [onCloseRef]);

  if (!question) {
    return (
      <div className={styles.skeleton}>
        <Skeleton width="100%" height="150px" />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <Questionnaire
        embedded
        question={question}
        beforeNextStepAction={beforeNextStepAction}
        targetQuestionNotFoundAction={targetQuestionNotFoundAction}
      />
    </div>
  );
}, (
  { questions: prevQuestions, onSave: prevOnSave, onClose: prevOnClose },
  { questions: nextQuestions, onSave: nextOnSave, onClose: nextOnClose },
) => (
  prevQuestions === nextQuestions
  && prevOnSave === nextOnSave
  && prevOnClose === nextOnClose
));

QuestionnairePopup.propTypes = {
  questions: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};

export default QuestionnairePopup;
